<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_event_management')"
                        :isNewButton="true"
                        @new-button-click="showCreateModal"
                        @filter-div-status="datatable.filterStatus = $event">
                </Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_event_management')"
                              :isNewButton="true"
                              @new-button-click="showCreateModal"
                              @filter-div-status="datatable.filterStatus = $event">
                </HeaderMobile>
            </template>

            <datatable-filter v-show="datatable.filterStatus"
                              @filter="filter"
                              @filterClear="filterClear"
                              :export-excel="true"
                              @exportExcel="exportExcel">
                <b-row>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('coop_company')">
                            <coop-company-selectbox v-model="datatable.queryParams.filter.company_id" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('coop_event_format')">
                            <parameter-selectbox code="coop_event.formats" v-model="datatable.queryParams.filter.format" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('status')">
                            <parameter-selectbox code="coop_event.statuses" v-model="datatable.queryParams.filter.status" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('academic_year')">
                            <academic-years-selectbox v-model="datatable.queryParams.filter['semester.academic_year']" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('semester')">
                            <semesters-selectbox v-model="datatable.queryParams.filter.semester_id" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('start_date')">
                            <SelectDate v-model="datatable.queryParams.filter.event_date_after" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />

            <CommonModal ref="createModal" size="lg" :onHideOnlyX="true" @bHideModalHeaderClose="formClear">
                <template v-slot:CommonModalTitle>
                    {{ selectedEvent ? $t('edit') : $t('coop_event_new') }}
                </template>
                <template v-slot:CommonModalContent>
                    <create-form v-if="showCreate" :eventData="selectedEvent" @actionSuccess="onActionSuccess" @cancel="createModalHide" />
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>
<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import AcademicYearsSelectbox from '@/components/interactive-fields/AcademicYearsSelectbox';
    import SemestersSelectbox from '@/components/interactive-fields/SemestersSelectbox';
    import ParameterSelectbox from '@/components/interactive-fields/ParameterSelectbox';
    import CoopCompanySelectbox from '@/components/interactive-fields/CoopCompanySelectbox.vue';
    import SelectDate from '@/components/interactive-fields/SelectDate';

    //Page
    import CreateForm from './CreateForm';

    //Service
    import CoopEventService from '@/services/CoopEventService';

    //Other
    import qs from 'qs';

    export default {
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            DatatableFilter,
            Datatable,
            CommonModal,
            AcademicYearsSelectbox,
            SemestersSelectbox,
            ParameterSelectbox,
            CoopCompanySelectbox,
            SelectDate,
            CreateForm
        },
        metaInfo() {
            return {
                title: this.$t('coop_event_management')
            };
        },
        data() {
            return {
                showCreate: false,
                selectedEvent: null,
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('edit'),
                                    class: 'ri-pencil-line',
                                    callback: (row) => {
                                        this.editEvent(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_event_view_applications'),
                                    class: 'ri-file-list-3-line',
                                    callback: (row) => {
                                        this.$router.push('/coop/admin/event/' + row.id + '/applications');
                                    }
                                },
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-2-line',
                                    callback: (row) => {
                                        this.deleteEvent(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('coop_event_name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_company'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_type'),
                            field: this.getLocaleField('type_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'event_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_time'),
                            field: 'start_time',
                            formatFn: (val, row) => (row.start_time && row.end_time) ? `${row.start_time.substring(0, 5)} - ${row.end_time.substring(0, 5)}` : '',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_format'),
                            field: this.getLocaleField('format_name'),
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('application_count'),
                            field: 'application_count',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('approved_application_count'),
                            field: 'approved_application_count',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_quota'),
                            field: 'quota',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('remaining_quota'),
                            field: 'remaining_quota',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: 'status_name',
                            formatFn: (val, row) => val || row.status || '',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    filterStatus: true,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getRows();
        },
        methods: {
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.getRows();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {...this.datatable.queryParams},
                    paramsSerializer: (params) => qs.stringify(params, {encode: false})
                };

                return CoopEventService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            showCreateModal() {
                this.selectedEvent = null;
                this.showCreate = true;
                this.$refs.createModal.$refs.commonModal.show();
            },
            editEvent(row) {
                this.selectedEvent = row;
                this.showCreate = true;
                this.$refs.createModal.$refs.commonModal.show();
            },
            createModalHide() {
                this.$refs.createModal.$refs.commonModal.hide();
                this.formClear();
            },
            formClear() {
                this.showCreate = false;
                this.selectedEvent = null;
            },
            onActionSuccess() {
                this.createModalHide();
                this.getRows();
            },
            deleteEvent(row) {
                this.$swal.fire({
                    text: this.$t('are_you_sure_to_delete'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopEventService.remove(row.id).then((response) => {
                            this.$toast.success(this.$t('api.' + (response.data.message || 'success')));
                            this.getRows();
                        });
                    }
                });
            },
            exportExcel() {
                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, {encode: false})
                };

                CoopEventService.excelExport(config)
                    .then((response) => {
                        this._downloadFile(response, 'Events.xlsx');
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            }
        }
    };
</script>
